import { InvitationCollectionActionTaken } from '../enums/invitation-collection.enum'
import { InvitationCollectionItem } from '../types/invitation-tracker.type'

type InvitationActionTakenDisplay = {
  label: string
  badgeClass: string
}

const INVITATION_ACTION_TAKEN_DISPLAY: Record<InvitationCollectionActionTaken, InvitationActionTakenDisplay> = {
  [InvitationCollectionActionTaken.REGISTERED]: {
    label: 'Registered',
    badgeClass: 'tw-bg-green-100 tw-text-green-800'
  },
  [InvitationCollectionActionTaken.PENDING]: {
    label: 'Pending',
    badgeClass: 'tw-bg-yellow-100 tw-text-yellow-800'
  },
  [InvitationCollectionActionTaken.DECLINED]: {
    label: 'Declined',
    badgeClass: 'tw-bg-red-100 tw-text-red-800'
  },
  [InvitationCollectionActionTaken.UNREACHABLE]: {
    label: 'Unreachable',
    badgeClass: 'tw-bg-red-100 tw-text-red-800'
  },
  [InvitationCollectionActionTaken.NEEDS_ATTENTION]: {
    label: 'Needs attention',
    badgeClass: 'tw-bg-yellow-100 tw-text-yellow-800'
  }
}

const INVITATION_COLLECTION_ACTION_TAKEN_VALUES = new Set<InvitationCollectionActionTaken>(Object.values(InvitationCollectionActionTaken))

/**
 * Returns true when the invitee already has an action-taken status from the API.
 * @param actionTaken {InvitationCollectionActionTaken | null | undefined} - API action taken value.
 */
export function hasInvitationCollectionActionTaken(actionTaken: InvitationCollectionActionTaken | null | undefined): boolean {
  if (actionTaken == null) return false
  return INVITATION_COLLECTION_ACTION_TAKEN_VALUES.has(actionTaken)
}

/**
 * Returns true when any channel has at least one invitation send on the collection item.
 * @param item {InvitationCollectionItem} - Collection item from the API.
 */
export function hasInvitationCollectionChannelInvite(item: InvitationCollectionItem): boolean {
  const channels = item.invite_channel
  return Boolean(channels.EMAIL.length || channels.SMS.length || channels.WHATSAPP.length)
}

/**
 * Returns true when remove is allowed: no action taken and no channel invites yet
 * (including failed sends — any invite history hides remove).
 * @param item {InvitationCollectionItem} - Collection item from the API.
 */
export function canRemoveInvitationCollectionItem(item: InvitationCollectionItem): boolean {
  return !hasInvitationCollectionActionTaken(item.action_taken) && !hasInvitationCollectionChannelInvite(item)
}

/**
 * Formats the Action taken badge label.
 * @param actionTaken {InvitationCollectionActionTaken} - API action taken value.
 */
export function formatInvitationCollectionActionTakenLabel(actionTaken: InvitationCollectionActionTaken | null | undefined): string {
  if (actionTaken == null) return ''
  return INVITATION_ACTION_TAKEN_DISPLAY[actionTaken]?.label ?? ''
}

/**
 * Returns Tailwind classes for an Action taken badge.
 * @param actionTaken {InvitationCollectionActionTaken} - API action taken value.
 */
export function invitationCollectionActionTakenBadgeClass(actionTaken: InvitationCollectionActionTaken | null | undefined): string {
  if (actionTaken == null) return ''
  return INVITATION_ACTION_TAKEN_DISPLAY[actionTaken]?.badgeClass ?? ''
}
